import { CheckCircle2, Info, ThumbsDown, ThumbsUp, XCircle } from "lucide-react";
import { Button } from "../../components/ui/button";

export interface Suggestion {
  id: string;
  section: string;
  original: string;
  suggested: string;
  reason: string;
}

interface SuggestionState {
  [id: string]: boolean | null; // true=accepted, false=rejected, null=undecided
}

interface Props {
  suggestions: Suggestion[];
  flags: string[];
  states: SuggestionState;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
}

function SuggestionCard({ suggestion, state, onAccept, onReject }: {
  suggestion: Suggestion;
  state: boolean | null;
  onAccept: () => void;
  onReject: () => void;
}) {
  const decided   = state !== null;
  const accepted  = state === true;
  const rejected  = state === false;

  return (
    <div className={`rounded-lg border p-4 transition-colors ${
      accepted ? "border-green-300 bg-green-50" :
      rejected ? "border-gray-200 bg-gray-50 opacity-60" :
      "border-gray-200 bg-white"
    }`}>
      {/* Section badge */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-medium">
          {suggestion.section}
        </span>
        {decided && (
          <span className={`text-xs font-medium flex items-center gap-1 ${accepted ? "text-green-700" : "text-gray-400"}`}>
            {accepted ? <><CheckCircle2 className="w-3 h-3" /> Accepted</> : <><XCircle className="w-3 h-3" /> Rejected</>}
          </span>
        )}
      </div>

      {/* Tracked change display */}
      <div className="space-y-2 mb-3">
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">Original</div>
        <p className="text-sm text-red-600 line-through leading-relaxed">{suggestion.original}</p>
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mt-2">Suggested</div>
        <p className={`text-sm leading-relaxed ${accepted ? "text-green-800 font-medium" : "text-green-700"}`}>
          {suggestion.suggested}
        </p>
      </div>

      {/* Reason */}
      <p className="text-xs text-gray-400 italic mb-3">{suggestion.reason}</p>

      {/* Actions */}
      {!decided && (
        <div className="flex gap-2">
          <button
            onClick={onAccept}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs py-1.5 rounded border border-green-300 text-green-700 hover:bg-green-50 transition-colors font-medium"
          >
            <ThumbsUp className="w-3 h-3" /> Accept
          </button>
          <button
            onClick={onReject}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs py-1.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
          >
            <ThumbsDown className="w-3 h-3" /> Reject
          </button>
        </div>
      )}
      {decided && (
        <Button variant="outline" size="sm" onClick={accepted ? onReject : onAccept}>
          {accepted ? "Undo acceptance" : "Reconsider"}
        </Button>
      )}
    </div>
  );
}

export default function SuggestionsPanel({ suggestions, flags, states, onAccept, onReject, onAcceptAll, onRejectAll }: Props) {
  const total    = suggestions.length;
  const accepted = Object.values(states).filter((v) => v === true).length;
  const rejected = Object.values(states).filter((v) => v === false).length;
  const pending  = total - accepted - rejected;

  if (total === 0 && flags.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CheckCircle2 className="w-8 h-8 text-green-500 mb-3" />
        <p className="text-sm font-medium text-gray-700">No improvements suggested</p>
        <p className="text-xs text-gray-400 mt-1">The resume looks strong as-is.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span><strong className="text-gray-900">{total}</strong> suggestion{total !== 1 ? "s" : ""}</span>
          {accepted > 0 && <span className="text-green-700"><strong>{accepted}</strong> accepted</span>}
          {rejected > 0 && <span className="text-gray-400"><strong>{rejected}</strong> rejected</span>}
          {pending > 0 && <span><strong>{pending}</strong> pending</span>}
        </div>
        {pending > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={onAcceptAll}
              className="text-xs px-2.5 py-1 rounded border border-green-300 text-green-700 hover:bg-green-50 transition-colors font-medium"
            >
              Accept All
            </button>
            <button
              onClick={onRejectAll}
              className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
            >
              Reject All
            </button>
          </div>
        )}
      </div>

      {/* Flags / observations */}
      {flags.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Info className="w-4 h-4 text-amber-600" />
            <p className="text-xs font-medium text-amber-800 uppercase tracking-wide">Observations</p>
          </div>
          <ul className="space-y-1">
            {flags.map((flag, i) => (
              <li key={i} className="text-xs text-amber-700 flex items-start gap-1.5">
                <span className="mt-0.5">•</span> {flag}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Suggestion cards */}
      <div className="space-y-3">
        {suggestions.map((s) => (
          <SuggestionCard
            key={s.id}
            suggestion={s}
            state={states[s.id] ?? null}
            onAccept={() => onAccept(s.id)}
            onReject={() => onReject(s.id)}
          />
        ))}
      </div>
    </div>
  );
}
